// @ts-nocheck -- follows the surrounding not-yet-typed src/app modules.
import { loadAircraftArtView } from "../core/preferences.js";
// Default aircraft artwork (stylized line-art defaults).
//
// When an aircraft has no uploaded photo, the workspace photo frame shows a
// generated line-art default resolved from the aircraft's make/model: a
// top-down planform view by default, swapping to the stacked side/front view
// while hovered. The art catalog comes from GET /api/default-aircraft-images
// (public metadata: keys, labels, per-view URLs) and the image URLs are
// public, so a plain <img src> can load them without the auth-blob machinery
// user photos need. Light theme inverts the monochrome art in CSS
// (styles.css .is-default-art), so this module never touches theming.

// Ordered candidate image keys for a make/model. Pure so tests can cover the
// mapping without a DOM. First match wins; every list ends with the archetype
// fallback chain so an aircraft always resolves to something once the
// catalog rows have generated images.
const MODEL_RULES = [
  // Cessna singles. Registry model strings carry letter prefixes/suffixes
  // (R172K, A185F, T210N, 182Q), so the number patterns allow them; the
  // context pattern (usually the make) keeps bare numbers like the "150" in
  // "PA-18-150" from matching for other makes.
  ["cessna-150-152", /\b[a-z]{0,2}15[02][a-z]{0,3}\b/, /cessna/],
  ["cessna-120-140", /\b[a-z]{0,2}1[24]0[a-z]{0,3}\b/, /cessna/],
  ["cessna-170", /\b[a-z]{0,2}170[a-z]{0,3}\b/, /cessna/],
  ["cessna-172", /\b[a-z]{0,2}17[25][a-z]{0,3}\b|skyhawk/, /cessna|skyhawk/],
  ["cessna-177", /\b[a-z]{0,2}177[a-z]{0,3}\b|cardinal/, /cessna|cardinal/],
  ["cessna-180", /\b[a-z]{0,2}180[a-z]{0,3}\b/, /cessna/],
  ["cessna-182", /\b[a-z]{0,2}182[a-z]{0,3}\b|skylane/, /cessna|skylane/],
  ["cessna-185", /\b[a-z]{0,2}185[a-z]{0,3}\b|skywagon/, /cessna|skywagon/],
  ["cessna-195", /\b[a-z]{0,2}19[05][a-z]{0,3}\b/, /cessna/],
  ["cessna-206", /\b[a-z]{0,2}20[567][a-z]{0,3}\b|stationair/, /cessna|stationair/],
  ["cessna-210", /\b[a-z]{0,2}210[a-z]{0,3}\b|centurion/, /cessna|centurion/],
  // Piper.
  ["piper-j3-cub", /\bj-?3\b|\bj3c\b|\bpa-?11\b/, /piper|cub/],
  ["piper-pa12", /\bpa-?1[24]\b|super cruiser/, /piper|pa-?1[24]/],
  ["piper-pa18", /\bpa-?18\b|super cub/, /piper|pa-?18|cub/],
  ["piper-pa22", /\bpa-?22\b|tri-?pacer|\bcolt\b/, /piper|pa-?22/],
  ["piper-pa24", /\bpa-?24\b|comanche/, /piper|pa-?24/],
  ["piper-twin-comanche", /\bpa-?3[09]\b|twin comanche/, /piper|pa-?3[09]/],
  ["piper-pa28r", /\bpa-?28r|arrow/, /piper|pa-?28/],
  ["piper-pa28", /\bpa-?28\b|cherokee|warrior|archer|dakota/, /piper|pa-?28/],
  ["piper-pa32", /\bpa-?32\b|saratoga|cherokee six|lance/, /piper|pa-?32/],
  ["piper-pa34", /\bpa-?34\b|seneca/, /piper|pa-?34/],
  // Beechcraft.
  ["beech-bonanza-v35", /v-?35|v-?tail/, /beech|bonanza/],
  ["beech-bonanza-33-36", /bonanza|debonair|\b[a-z]?3[36][a-z]?\b/, /beech|bonanza|debonair/],
  ["beech-baron", /baron|\b[a-z]?(?:55|58)[a-z]?\b/, /beech|baron/],
  ["beech-t34", /\bt-?34\b|mentor/, /beech|t-?34/],
  // Van's RV. Tailwheel vs nosewheel splits on the A suffix.
  ["rv-3-4", /\brv-?[34]\b/, /van|rv-?[34]/],
  ["rv-6a-7a-9a", /\brv-?[679]a\b/, /van|rv/],
  ["rv-6-7-9-tw", /\brv-?[679]\b/, /van|rv/],
  ["rv-8", /\brv-?8a?\b/, /van|rv/],
  ["rv-10", /\brv-?10\b/, /van|rv/],
  ["rv-12-14", /\brv-?1[24]a?\b/, /van|rv/],
  // Classic taildraggers.
  ["aeronca-champ", /champ\b|\b7ac\b|\b7[a-z]c\b/, /aeronca|champion|champ/],
  ["aeronca-chief", /chief|\b11ac\b/, /aeronca/],
  ["citabria-decathlon", /citabria|decathlon|\b7eca\b|\b7gcbc\b|\b8kcab\b/, /champion|bellanca|citabria|decathlon/],
  ["scout-husky", /\bscout\b|husky|\ba-?1[abc]?\b/, /aviat|champion|husky|scout/],
  ["maule", /\bm-?[4-9]\b|\bmx-?7\b|\bmt-?7\b/, /maule/],
  ["stinson-108", /\b108\b|voyager|station wagon/, /stinson/],
  ["luscombe-8", /\b8[a-f]?\b|silvaire/, /luscombe/],
  ["taylorcraft-bc12", /\bbc-?12\b|\bbc\b|\bbl\b/, /taylorcraft/],
  ["ercoupe", /ercoupe|\b415\b/, /ercoupe|erco|forney|alon|mooney m-?10/],
  // Modern singles.
  ["cirrus-sr", /\bsr-?2[02]t?\b/, /cirrus|sr-?2/],
  ["mooney-m20", /\bm-?20[a-z]?\b|ovation|acclaim|bravo/, /mooney/],
  ["grumman-aa5", /\baa-?[15][ab]?\b|tiger|cheetah|traveler|yankee/, /grumman|american general|gulfstream american/],
  ["diamond-da20", /\bda-?20\b|katana/, /diamond/],
  ["diamond-da40", /\bda-?40\b|diamond star/, /diamond/],
  ["columbia-350-400", /\b(?:lc4[012]|30[05]|350|400)\b|columbia/, /columbia|lancair|cessna/],
  // Sport, bush, iconic.
  ["carbon-cub", /carbon cub|\bcc1[189]\b|\bex-?[23]\b|top cub/, /cubcrafters|carbon/],
  ["kitfox-avid", /kitfox|avid/, /./],
  ["zenith-stol", /\bch-?7[05][01]\b|zodiac|cruzer/, /zenith|zenair/],
  ["stearman", /stearman|\bpt-?1[37]\b|\ba75\b|kaydet/, /./],
  ["pitts", /pitts|\bs-?[12][a-z]?\b/, /pitts|aviat/],
  ["dhc2-beaver", /beaver|\bdhc-?2\b/, /havilland|dhc|viking/],
  ["navion", /navion/, /./],
  ["globe-swift", /swift|\bgc-?1[ab]?\b/, /globe|temco|swift/],
];

// Keyword-only archetype hints. Without registry data (gear/wing/engine
// count) most aircraft can't be classified from strings alone, so unmatched
// aircraft fall through to generic-fallback.
const ARCHETYPE_RULES = [
  ["glider", /glider|sailplane|\bask-?\d|\blak-?\d|schleicher|schempp|\bds-?\d/],
  ["helicopter", /helicopter|\br-?(?:22|44|66)\b|robinson|enstrom|\bbell \d/],
  ["gyroplane-ultralight", /gyro|autogyro|trike|quicksilver|challenger ii/],
  ["biplane-open-cockpit", /biplane|waco|great lakes|\bstarduster\b|skybolt/],
  ["canard-pusher", /long-?ez|vari-?eze|velocity|cozy|canard/],
  ["floatplane", /float|seaplane/],
  ["amphibian-flying-boat", /amphib|searey|\bicon a5\b|seabee|\blake la/],
  ["warbird-trainer", /\bt-?6\b|texan|harvard|\byak-?\d|warbird|mustang|\bl-?39\b/],
  ["single-turboprop", /\bpc-?12\b|\btbm\b|kodiak|caravan|meridian/],
  ["twin-turboprop", /king air|\bbe-?(?:90|100|200|300)\b|cheyenne|conquest/],
  ["light-jet", /citation|\bjet\b|eclipse 500|vision jet|phenom/],
  ["twin-piston", /twin|seneca|seminole|aztec|\b310\b.*cessna|apache|travel air|duchess/],
  // Classic fabric-and-tube makes whose specific model art may not exist yet:
  // fall back to the strut-braced taildragger archetype instead of generic.
  ["high-wing-strut-taildragger", /\bcub\b|champ\b|citabria|decathlon|husky|maule|luscombe|taylorcraft|stinson|aeronca|\bscout\b|pacer|porterfield|interstate|rearwin|\bfunk\b/],
];

// Human-readable coverage per catalog entry: which aircraft models WOULD
// resolve to this art (per MODEL_RULES / ARCHETYPE_RULES above), independent
// of what any account has registered today. Shown on the admin Debug >
// Aircraft Images table. Keep in sync when editing the rules; archetypes with
// no keyword rule are reachable only as future/manual assignments and say so
// via their airframe-class description.
export const DEFAULT_ART_MODEL_COVERAGE = {
  "cessna-150-152": "Cessna 150 / 152",
  "cessna-120-140": "Cessna 120 / 140",
  "cessna-170": "Cessna 170",
  "cessna-172": "Cessna 172 / 175 Skyhawk",
  "cessna-177": "Cessna 177 Cardinal",
  "cessna-180": "Cessna 180",
  "cessna-182": "Cessna 182 Skylane",
  "cessna-185": "Cessna 185 Skywagon",
  "cessna-195": "Cessna 190 / 195",
  "cessna-206": "Cessna 205 / 206 / 207 Stationair",
  "cessna-210": "Cessna 210 Centurion",
  "piper-j3-cub": "Piper J-3 Cub, PA-11",
  "piper-pa12": "Piper PA-12 / PA-14 Super Cruiser",
  "piper-pa18": "Piper PA-18 Super Cub",
  "piper-pa22": "Piper PA-22 Tri-Pacer / Colt",
  "piper-pa24": "Piper PA-24 Comanche",
  "piper-twin-comanche": "Piper PA-30 / PA-39 Twin Comanche",
  "piper-pa28r": "Piper PA-28R Arrow",
  "piper-pa28": "Piper PA-28 Cherokee / Warrior / Archer / Dakota",
  "piper-pa32": "Piper PA-32 Cherokee Six / Lance / Saratoga",
  "piper-pa34": "Piper PA-34 Seneca",
  "beech-bonanza-v35": "Beechcraft V35 Bonanza (V-tail)",
  "beech-bonanza-33-36": "Beechcraft Bonanza 33 / 36, Debonair",
  "beech-baron": "Beechcraft Baron 55 / 58",
  "beech-t34": "Beechcraft T-34 Mentor",
  "rv-3-4": "Van's RV-3 / RV-4",
  "rv-6a-7a-9a": "Van's RV-6A / 7A / 9A",
  "rv-6-7-9-tw": "Van's RV-6 / 7 / 9 (tailwheel)",
  "rv-8": "Van's RV-8 / RV-8A",
  "rv-10": "Van's RV-10",
  "rv-12-14": "Van's RV-12 / RV-14",
  "aeronca-champ": "Aeronca 7AC Champ",
  "aeronca-chief": "Aeronca 11AC Chief",
  "citabria-decathlon": "Citabria / Decathlon (7ECA, 7GCBC, 8KCAB)",
  "scout-husky": "Champion Scout, Aviat Husky",
  "maule": "Maule M-4 through M-9, MX-7, MT-7",
  "stinson-108": "Stinson 108 Voyager / Station Wagon",
  "luscombe-8": "Luscombe 8 Silvaire",
  "taylorcraft-bc12": "Taylorcraft BC-12 / BC / BL",
  "ercoupe": "Ercoupe 415 (Erco, Forney, Alon, Mooney M-10)",
  "cirrus-sr": "Cirrus SR20 / SR22 / SR22T",
  "mooney-m20": "Mooney M20 (Ovation, Acclaim, Bravo)",
  "grumman-aa5": "Grumman AA-1 / AA-5 (Yankee, Traveler, Cheetah, Tiger)",
  "diamond-da20": "Diamond DA20 Katana",
  "diamond-da40": "Diamond DA40 Diamond Star",
  "columbia-350-400": "Columbia / Lancair 300 / 350 / 400 (Cessna LC40-LC42)",
  "carbon-cub": "CubCrafters Carbon Cub / Top Cub",
  "kitfox-avid": "Kitfox, Avid Flyer",
  "zenith-stol": "Zenith CH 701 / 750, Zodiac, Cruzer",
  "stearman": "Boeing-Stearman PT-13 / PT-17 Kaydet (A75)",
  "pitts": "Pitts S-1 / S-2",
  "dhc2-beaver": "de Havilland DHC-2 Beaver",
  "navion": "Navion",
  "globe-swift": "Globe / Temco GC-1 Swift",
  "glider": "Gliders and sailplanes (Schleicher, Schempp-Hirth, LAK...)",
  "helicopter": "Helicopters (Robinson R22 / R44 / R66, Enstrom, Bell...)",
  "gyroplane-ultralight": "Gyroplanes, trikes, and ultralights",
  "biplane-open-cockpit": "Biplanes (Waco, Great Lakes, Starduster, Skybolt...)",
  "canard-pusher": "Canard pushers (Long-EZ, VariEze, Velocity, Cozy)",
  "floatplane": "Floatplanes and seaplanes",
  "amphibian-flying-boat": "Amphibians and flying boats (SeaRey, Icon A5, Seabee, Lake...)",
  "warbird-trainer": "Warbird trainers (T-6 Texan / Harvard, Yak, L-39...)",
  "single-turboprop": "Single turboprops (PC-12, TBM, Kodiak, Caravan, Meridian)",
  "twin-turboprop": "Twin turboprops (King Air, Cheyenne, Conquest)",
  "light-jet": "Light jets (Citation, Eclipse 500, Vision Jet, Phenom...)",
  "twin-piston": "Piston twins (Seneca, Seminole, Aztec, Apache, Duchess...)",
  "high-wing-strut-taildragger": "Classic fabric taildraggers without model-specific art (Cub, Champ, Pacer, Porterfield...)",
  "high-wing-strut-tricycle": "High-wing strut-braced tricycle singles (no keyword rule yet)",
  "high-wing-cantilever-tricycle": "High-wing cantilever singles (no keyword rule yet)",
  "high-wing-stol-taildragger": "High-wing STOL bush taildraggers (no keyword rule yet)",
  "low-wing-tricycle": "Low-wing tricycle singles (no keyword rule yet)",
  "low-wing-taildragger": "Low-wing taildragger singles (no keyword rule yet)",
  "low-wing-composite-speedster": "Low-wing composite kit speedsters (no keyword rule yet)",
  "aerobatic-monoplane": "Competition aerobatic monoplanes (no keyword rule yet)",
  "motor-glider": "Motor gliders (no keyword rule yet)",
  "generic-fallback": "Any aircraft not matched by another category (brand art)",
};

export function resolveDefaultAircraftArtKeys(make, model) {
  const makeText = String(make ?? "").trim().toLowerCase();
  const combined = `${makeText} ${String(model ?? "").trim().toLowerCase()}`.trim();
  const keys = [];
  if (combined) {
    for (const [key, modelPattern, contextPattern] of MODEL_RULES) {
      if (modelPattern.test(combined) && contextPattern.test(combined)) {
        keys.push(key);
        break;
      }
    }
    for (const [key, pattern] of ARCHETYPE_RULES) {
      if (pattern.test(combined)) {
        keys.push(key);
        break;
      }
    }
  }
  keys.push("generic-fallback");
  return keys;
}

export function createAircraftDefaultArt({
  requestAircraftWorkspaceRender = () => {},
  fetchImpl = (...args) => fetch(...args),
} = {}) {
  // imageKey -> { label, views: { top_down, side_views } }
  let catalog = null;
  let catalogLoading = false;
  let catalogFailed = false;

  function loadCatalog() {
    if (catalog || catalogLoading || catalogFailed) return;
    catalogLoading = true;
    (async () => {
      try {
        const response = await fetchImpl("/api/default-aircraft-images");
        if (!response.ok) { catalogFailed = true; return; }
        const payload = await response.json();
        catalog = new Map();
        for (const entry of Array.isArray(payload?.images) ? payload.images : []) {
          if (entry?.imageKey) catalog.set(entry.imageKey, entry);
        }
        requestAircraftWorkspaceRender();
      } catch (error) {
        catalogFailed = true;
        console.warn("Default aircraft art catalog fetch failed", error);
      } finally {
        catalogLoading = false;
      }
    })();
  }

  function defaultArtForProfile(profile) {
    if (!catalog) {
      loadCatalog();
      return null;
    }
    const make = profile?.aircraft?.make;
    const model = profile?.aircraft?.model;
    for (const key of resolveDefaultAircraftArtKeys(make, model)) {
      const entry = catalog.get(key);
      if (entry?.views?.top_down) {
        return {
          imageKey: key,
          label: entry.label,
          topDownUrl: entry.views.top_down,
          sideViewsUrl: entry.views.side_views ?? null,
        };
      }
    }
    return null;
  }

  // Which view rests in the frame vs appears on hover follows the Account >
  // Settings "Aircraft image" preference (top-down by default).
  function restAndHoverUrls(art) {
    if (!art) return null;
    const preferSide = loadAircraftArtView() === "side_views" && art.sideViewsUrl;
    return {
      rest: preferSide ? art.sideViewsUrl : art.topDownUrl,
      hover: preferSide ? art.topDownUrl : art.sideViewsUrl,
      label: art.label,
    };
  }

  function bindHoverSwap(frameElement, imageElement) {
    if (!frameElement || frameElement.dataset.defaultArtHoverBound) return;
    frameElement.dataset.defaultArtHoverBound = "true";
    frameElement.addEventListener("mouseenter", () => {
      const hoverUrl = imageElement.dataset.defaultArtHover;
      if (hoverUrl && imageElement.classList.contains("is-default-art")) {
        imageElement.src = hoverUrl;
      }
    });
    frameElement.addEventListener("mouseleave", () => {
      const restUrl = imageElement.dataset.defaultArtRest;
      if (restUrl && imageElement.classList.contains("is-default-art")) {
        imageElement.src = restUrl;
      }
    });
  }

  // Called by the workspace renderer after the user-photo presentation pass.
  // A user photo always wins; the default art only fills an empty frame.
  function applyAircraftDefaultArt({ imageElement, fallbackElement, frameElement, profile, hasUserPhoto }) {
    if (!imageElement) return;
    if (hasUserPhoto) {
      imageElement.classList.remove("is-default-art");
      delete imageElement.dataset.defaultArtRest;
      delete imageElement.dataset.defaultArtHover;
      return;
    }
    const art = restAndHoverUrls(defaultArtForProfile(profile));
    if (!art) {
      imageElement.classList.remove("is-default-art");
      delete imageElement.dataset.defaultArtRest;
      delete imageElement.dataset.defaultArtHover;
      return;
    }
    imageElement.classList.add("is-default-art");
    imageElement.dataset.defaultArtRest = art.rest;
    if (art.hover) {
      imageElement.dataset.defaultArtHover = art.hover;
      // Preload so the hover swap is instant.
      const preload = new Image();
      preload.src = art.hover;
    } else {
      delete imageElement.dataset.defaultArtHover;
    }
    // Re-renders land here while hovered too; keep whichever view matches the
    // current hover state instead of forcing the resting view back.
    const shown = frameElement?.matches?.(":hover") && art.hover ? art.hover : art.rest;
    if (imageElement.getAttribute("src") !== shown) imageElement.src = shown;
    imageElement.alt = `${art.label} line art`;
    imageElement.hidden = false;
    if (fallbackElement) fallbackElement.hidden = true;
    bindHoverSwap(frameElement, imageElement);
  }

  // Direct URLs for list-style renderers (fleet cards, sidebar avatars) that
  // build <img> markup instead of mutating a live element. Rest/hover follow
  // the Account > Settings "Aircraft image" preference; the URLs are public,
  // so plain <img src> loads them without the auth-blob machinery user
  // photos need.
  function defaultArtRestUrl(profile) {
    return restAndHoverUrls(defaultArtForProfile(profile))?.rest ?? null;
  }

  function defaultArtHoverUrl(profile) {
    return restAndHoverUrls(defaultArtForProfile(profile))?.hover ?? null;
  }

  return { applyAircraftDefaultArt, defaultArtForProfile, defaultArtRestUrl, defaultArtHoverUrl };
}
